import React, { useEffect, useRef } from "react";
import { useThree } from "@react-three/fiber";
import {
  DirectionalLight,
  SpotLight,
  AmbientLight,
  Object3D,
  Color,
  Vector2,
} from "three";

const SHADOW_CAMERA_SIZE = 70;
const SHADOW_FAR = 230;
const SHADOW_NEAR = 10;
const SHADOWS_ENABLED = true;

function createLightObj({ type, position, intensity, color, name, castShadow, isMobile }) {
  let light;
  const colorObj = new Color(color || 0xffffff);

  if (type === "ambient") {
    light = new AmbientLight(colorObj, intensity ?? 1);
  } else if (type === "spot") {
    light = new SpotLight(colorObj, intensity ?? 1);
    light.angle = Math.PI / 4;
    light.penumbra = 1;
    light.decay = 0;
    light.distance = 200;
    light.castShadow = SHADOWS_ENABLED && (castShadow ?? true);
    light.shadow.mapSize = new Vector2(isMobile ? 128 : 1024, isMobile ? 128 : 1024);
    light.shadow.camera.near = SHADOW_NEAR;
    light.shadow.camera.far = SHADOW_FAR;
    const target = new Object3D();
    target.position.set(0, 0, 0);
    light.target = target;
    light.name = name || "Spot";
    light.position.set(...(position ?? [0, 150, 0]));
    return { light, target };
  } else {
    light = new DirectionalLight(colorObj, intensity ?? 1);
    light.castShadow = SHADOWS_ENABLED && (castShadow ?? true);
    light.shadow.mapSize = new Vector2(isMobile ? 256 : 1024, isMobile ? 256 : 1024);
    light.shadow.camera.near = SHADOW_NEAR;
    light.shadow.camera.far = SHADOW_FAR;
    light.shadow.camera.left = -SHADOW_CAMERA_SIZE;
    light.shadow.camera.right = SHADOW_CAMERA_SIZE;
    light.shadow.camera.top = SHADOW_CAMERA_SIZE;
    light.shadow.camera.bottom = -SHADOW_CAMERA_SIZE;
    light.shadow.bias = isMobile ? -0.02 : -0.002;
    light.shadow.normalBias = isMobile ? 0.005 : 0.01;
    light.position.set(...(position ?? [0, 50, 0]));
    light.name = name || "Directional";
  }
  return { light };
}

const SceneLights = ({ isMobile, materialFormData, debugLights, propsLightSettings }) => {
  const { scene } = useThree();
  const lightsRef = useRef([]);
  const helpersRef = useRef([]);

  function removeAllLightsAndHelpers() {
    lightsRef.current.forEach(({ light, target }) => {
      if (light) scene.remove(light);
      if (target) scene.remove(target);
    });
    helpersRef.current.forEach((helper) => {
      if (helper) scene.remove(helper);
    });
    lightsRef.current = [];
    helpersRef.current = [];
  }
useEffect(() => {
  console.group("SceneLights Configuration");
  console.log("Raw propsLightSettings:", propsLightSettings);
  console.log("isMobile:", isMobile);

  const settings = typeof propsLightSettings === 'string' 
    ? JSON.parse(propsLightSettings) 
    : propsLightSettings;

  console.log("Parsed settings:", settings);
  console.log("isMobile:", isMobile);

  const DESKTOP_DEFAULTS = [
    { type: "directional", color: "#fff", position: [-200, -86, 0], name: "Front", enabled: true, intensity: 2 },
    { type: "directional", color: "#fff", position: [80, 10, 80], name: "Left", enabled: true, intensity: 2 },
    { type: "directional", color: "#fff", position: [80, 80, -80], name: "Right", enabled: true, intensity: 2 },
    { type: "directional", color: "#fff", position: [-80, 0, 0], name: "Behind", enabled: true, intensity: 2 },
    { type: "directional", color: "#fff", position: [-80, 80, 80], name: "Behind Top Left", enabled: true, intensity: 2 },
    { type: "ambient", color: "#fff", name: "Ambient", enabled: true, intensity: 0.6 },
  ];

  const MOBILE_DEFAULTS = [
    { type: "directional", enabled: true, intensity: 4, color: "#fff", position: [100, 50, 60], name: "Key", castShadow: true },
    { type: "ambient", enabled: true, intensity: 1.5, color: "#fff", name: "Ambient" },
  ];

  let lightsConfig = [];
  let configSource = "defaults";

  // Utilise 'settings' au lieu de propsLightSettings ici
  if (Array.isArray(settings) && settings.length === 2) {
    console.log("ENTREEEEEEEEEEE");
    const isDesktopConfig = Array.isArray(settings[0]) && settings[0].every(item => item?.type);
    const isMobileConfig = Array.isArray(settings[1]) && settings[1].every(item => item?.type);

    if (isDesktopConfig && isMobileConfig) {
      lightsConfig = isMobile ? settings[1] : settings[0];
      configSource = isMobile ? "mobile-preset" : "desktop-preset";
    }
  } else if (Array.isArray(settings) && settings.every(item => item?.type)) {
    lightsConfig = settings;
    configSource = "direct-config";
  }

  if (!lightsConfig.length) {
    const materialLights = materialFormData?.color?.lights || materialFormData?.lights;
    if (Array.isArray(materialLights)) {
      lightsConfig = materialLights;
      configSource = "materialFormData";
    }
  }

  if (!lightsConfig.length) {
    lightsConfig = isMobile ? MOBILE_DEFAULTS : DESKTOP_DEFAULTS;
  }

  console.log("Configuration source:", configSource);
  console.log("Active configuration:", lightsConfig);

  // Nettoyage des lumières existantes
  lightsRef.current.forEach(({ light, target }) => {
    scene.remove(light);
    if (target) scene.remove(target);
  });
  lightsRef.current = [];

  // Création des nouvelles lumières
  lightsRef.current = lightsConfig
    .filter(light => light.enabled !== false)
    .map(conf => {
      const { light, target } = createLightObj({ ...conf, isMobile });
      scene.add(light);
      if (target) scene.add(target);
      return { light, target };
    });

  console.groupEnd();

  return () => {
    lightsRef.current.forEach(({ light, target }) => {
      scene.remove(light);
      if (target) scene.remove(target);
    });
    lightsRef.current = [];
  };
}, [scene, isMobile, propsLightSettings, materialFormData?.color?.lights, materialFormData?.lights]);






  return null;
};

export default SceneLights;
